import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { FiX } from "react-icons/fi";
import {
    getHostels,
    getRooms,
    getAllocations,
    reallocateStudent,
} from "../../services/hostelService";

export default function ReallocateModal({ allocation, onClose, onSuccess }) {
    const [hostels, setHostels] = useState([]);
    const [selectedHostel, setSelectedHostel] = useState(allocation.hostel._id);
    const [rooms, setRooms] = useState([]);
    const [selectedRoom, setSelectedRoom] = useState("");
    const [takenBeds, setTakenBeds] = useState([]);
    const [selectedBed, setSelectedBed] = useState("");
    const [remarks, setRemarks] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        getHostels()
            .then((res) => setHostels(res.data || []))
            .catch(() => toast.error("Failed to load hostels"));
    }, []);

    useEffect(() => {
        if (!selectedHostel) return;
        getRooms(selectedHostel)
            .then((res) =>
                setRooms(
                    (res.data || []).filter(
                        (r) =>
                            r.status !== "Maintenance" &&
                            (r.occupiedBeds < r.capacity || r._id === allocation.room._id),
                    ),
                ),
            )
            .catch(() => toast.error("Failed to load rooms"));
    }, [selectedHostel]); // eslint-disable-line

    useEffect(() => {
        if (!selectedRoom) {
            setTakenBeds([]);
            return;
        }
        getAllocations({ room: selectedRoom, status: "Active" })
            .then((res) =>
                // NOTE: parens are required here — `res.data || [].filter(...)` (no parens)
                // would short-circuit to res.data directly whenever it's non-empty, skipping
                // the filter entirely and leaving the student's own current bed marked as
                // taken when reallocating within the same room.
                setTakenBeds(
                    (res.data || [])
                        .filter((a) => a._id !== allocation._id)
                        .map((a) => a.bedNumber),
                ),
            )
            .catch(() => toast.error("Failed to check bed availability"));
    }, [selectedRoom]); // eslint-disable-line

    const roomObj = rooms.find((r) => r._id === selectedRoom);
    const availableBeds = roomObj
        ? Array.from({ length: roomObj.capacity }, (_, i) => i + 1).filter(
            (n) => !takenBeds.includes(n),
        )
        : [];

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!selectedRoom || !selectedBed) {
            return toast.error("Select a room and bed");
        }

        setSaving(true);
        try {
            await reallocateStudent(allocation._id, {
                hostel: selectedHostel,
                room: selectedRoom,
                bedNumber: Number(selectedBed),
                academicYear: allocation.academicYear,
                remarks,
            });
            toast.success("Student reallocated successfully");
            onSuccess();
        } catch (err) {
            toast.error(err.response?.data?.message || "Failed to reallocate student");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal modal-lg" onClick={(e) => e.stopPropagation()}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.5rem" }}>
                    <h2>Reallocate {allocation.student.fullName}</h2>
                    <button type="button" className="btn btn-icon" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <p className="form-hint" style={{ marginBottom: "1rem" }}>
                    Currently in {allocation.hostel.name}, Room {allocation.room.roomNumber},
                    Bed {allocation.bedNumber}.
                </p>

                <form onSubmit={handleSubmit}>
                    <div className="form-grid">
                        <div className="form-group">
                            <label className="form-label">New Hostel</label>
                            <select
                                className="form-control"
                                value={selectedHostel}
                                onChange={(e) => {
                                    setSelectedHostel(e.target.value);
                                    setSelectedRoom("");
                                    setSelectedBed("");
                                }}
                            >
                                {hostels.map((h) => (
                                    <option key={h._id} value={h._id}>
                                        {h.name} ({h.type})
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                New Room<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <select
                                className="form-control"
                                value={selectedRoom}
                                onChange={(e) => {
                                    setSelectedRoom(e.target.value);
                                    setSelectedBed("");
                                }}
                            >
                                <option value="">Select room</option>
                                {rooms.map((r) => (
                                    <option key={r._id} value={r._id}>
                                        Room {r.roomNumber} ({r.occupiedBeds}/{r.capacity} filled)
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group">
                            <label className="form-label">
                                New Bed<span style={{ color: "var(--danger)" }}> *</span>
                            </label>
                            <select
                                className="form-control"
                                value={selectedBed}
                                onChange={(e) => setSelectedBed(e.target.value)}
                                disabled={!selectedRoom}
                            >
                                <option value="">
                                    {selectedRoom ? "Select bed" : "Select room first"}
                                </option>
                                {availableBeds.map((n) => (
                                    <option key={n} value={n}>
                                        Bed {n}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className="form-group" style={{ gridColumn: "1 / -1" }}>
                            <label className="form-label">Remarks</label>
                            <input
                                className="form-control"
                                value={remarks}
                                onChange={(e) => setRemarks(e.target.value)}
                                placeholder="Optional reason for reallocation"
                            />
                        </div>
                    </div>

                    <div className="modal-actions" style={{ marginTop: "1.25rem" }}>
                        <button type="button" className="btn btn-outline" onClick={onClose}>
                            Cancel
                        </button>
                        <button type="submit" className="btn btn-primary" disabled={saving}>
                            {saving ? "Saving..." : "Reallocate"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}